/**
 * ⚠️ 測試用，正式上線前整支刪除（OP#1855）
 * ─────────────────────────────────────────────────────────────────────────────
 * 後端的 useMockData 假資料不是為了驗 UI 做的，直接接上去有兩個問題：
 *
 * 1. egmId 是假的，對不到這間店實際的 pcbId → 機台名稱顯示成一串 id，
 *    單一機台範圍的清單永遠是空的
 * 2. 異常與修正各回 100 筆、每 2 小時一筆，涵蓋整個查詢區間 → 帳務查詢的
 *    每一個日列都同時有待確認與已修正，兩個記號變成恆亮，等於沒有標示作用
 *
 * 這個檔案只做兩件事：把 egmId 指到實際機台、把筆數抽稀，全部依內容雜湊，
 * 重新整理不會變。
 *
 * **不補後端沒給的值。** 判定狀態、操作者、修正的 operateType 一律照後端回的
 * 原樣呈現——假資料目前全是 state=0、operatorName 空字串，所以「已處理」區塊
 * 與修正歷程本來就會是空的，那是後端假資料的實況，不是畫面壞掉。
 *
 * 拔除方式：刪掉這個檔案與它的 __tests__，移除所有呼叫端
 * （搜尋 MOCK_EGM_MAPPING 可找到），並把 queries.ts 的 useMockData 改成寫死 false。
 */
import type {
  LedgerAbnormalResType,
  LedgerCorrectionResType
} from '@/models/types/ledgerAnomaly.types'

/**
 * 後端假資料開關。三支 GET 都支援 useMockData，由 queries.ts 的 queryFn 統一帶入。
 *
 * 刻意定義在這個「之後要刪掉」的檔案裡：接上真資料時改成 false 或刪檔，
 * queries.ts 會立刻編不過，不會有「假資料關了、機台重新指派卻還開著」的半開狀態。
 */
export const USE_MOCK_LEDGER_ANOMALY: boolean = true

/** 跟著假資料開關走 */
export const MOCK_EGM_MAPPING_ENABLED: boolean = USE_MOCK_LEDGER_ANOMALY

/**
 * 以內容做雜湊當亂數種子。
 *
 * 不能用 Math.random()：每次 render 都重算的話同一筆異常會跳到不同機台，
 * 清單順序與機台名稱會一直閃動，根本沒辦法操作。
 */
const hash = (value: string): number => {
  let h = 0
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h
}

/**
 * 把假資料的 egmId 重新指派到實際的機台上。
 *
 * 同一筆異常（egmId + occurredAt）永遠落在同一台，重新整理也不會變。
 */
export function remapAnomaliesToMachines(
  anomalies: LedgerAbnormalResType[],
  pcbIds: string[]
): LedgerAbnormalResType[] {
  if (!MOCK_EGM_MAPPING_ENABLED || pcbIds.length === 0) return anomalies

  return anomalies.map((anomaly) => ({
    ...anomaly,
    egmId: pcbIds[hash(`${anomaly.egmId}${anomaly.occurredAt}`) % pcbIds.length]
  }))
}

/**
 * 少於這個筆數就不抽稀。
 *
 * 這個 shim 是為了馴服後端假資料的 100 筆，不是要改變任何一份小資料；
 * e2e 用 page.route 餵進來的幾筆 fixture 不該被這裡吃掉。
 */
const THIN_THRESHOLD = 20

/** 事件的唯一鍵，與 utils/ledger/ledgerEvents 的 eventKey 同義；這裡自己算避免相依 */
const keyOf = (event: { egmId: string; goodsId: string; occurredAt: string }): string =>
  `${event.egmId}|${event.goodsId}|${event.occurredAt}`

/** 依內容雜湊穩定地抽樣：salt 不同，兩份清單才不會每次都挑到同一批事件 */
const keepOneIn = (
  event: { egmId: string; goodsId: string; occurredAt: string },
  salt: string,
  n: number
): boolean => hash(`${salt}${keyOf(event)}`) % n === 0

/**
 * 假資料的異常事件抽稀。不抽稀的話每個日列都有待確認，⚠ 記號恆亮。
 *
 * 只挑掉多餘的筆數，留下來的每一筆原樣不動。
 */
export function thinMockAbnormals(list: LedgerAbnormalResType[]): LedgerAbnormalResType[] {
  if (!MOCK_EGM_MAPPING_ENABLED || list.length < THIN_THRESHOLD) return list

  return list.filter((item) => keepOneIn(item, 'abnormal', 9))
}

/**
 * 假資料的修正紀錄抽稀。
 *
 * 用不同的 salt，讓「有異常」與「有修正」不會完全重疊——兩個記號是獨立的分類。
 *
 * state 照後端回的原樣：假資料全是 0，會被 isManualAdjustment 濾掉，
 * 所以修正歷程會是空的。那是假資料的實況，不在這裡補成 5(Create)。
 */
export function thinMockCorrections(list: LedgerCorrectionResType[]): LedgerCorrectionResType[] {
  if (!MOCK_EGM_MAPPING_ENABLED || list.length < THIN_THRESHOLD) return list

  return list.filter((item) => keepOneIn(item, 'correction', 13))
}
